'use client';

import * as React from 'react';
import { ComponentProps, ComponentType, ReactNode } from 'react';
import Link from 'next/link';

export const NAV_FORWARD = 'nav-forward';
export const NAV_BACK = 'nav-back';

// React's <ViewTransition> ships in the React canary bundled with the Next.js
// App Router but is not in @types/react yet. Resolve it at runtime (canary
// exports `ViewTransition`, older channels `unstable_ViewTransition`) and
// no-op gracefully if absent.
const ReactAny = React as unknown as Record<string, unknown>;
const ViewTransitionImpl = (ReactAny.ViewTransition ??
  ReactAny.unstable_ViewTransition) as
  | ComponentType<{
      children: ReactNode;
      default?: string;
      update?: Record<string, string>;
    }>
  | undefined;

/**
 * Wraps routed content so hierarchical navigations slide horizontally
 * (200–300ms, native push/pop feel) while tab switches and all other
 * navigations stay instant (`default="none"`). The boundary stays mounted
 * across navigations, so route changes are "update" transitions; the classes
 * below are targeted by ::view-transition-old/new rules in globals.css,
 * scoped to `<lg` so desktop never slides.
 */
export function MobilePageTransition({ children }: { children: ReactNode }) {
  if (!ViewTransitionImpl) {
    return <>{children}</>;
  }

  return (
    <ViewTransitionImpl
      default="none"
      update={{
        [NAV_FORWARD]: 'vt-page-forward',
        [NAV_BACK]: 'vt-page-back',
        default: 'none'
      }}
    >
      {children}
    </ViewTransitionImpl>
  );
}

type NavLinkProps = ComponentProps<typeof Link> & {
  direction?: typeof NAV_FORWARD | typeof NAV_BACK;
};

/**
 * Link that tags its navigation with a view-transition type so
 * MobilePageTransition can slide. Use on hierarchical links (list row →
 * detail, back button); keep plain <Link> for tab switches.
 */
export function NavLink({ direction = NAV_FORWARD, ...props }: NavLinkProps) {
  return <Link transitionTypes={[direction]} {...props} />;
}
